import {
  hasValue,
  ConditionalSection,
  SectionTitle,
  InfoGrid,
  InfoItem,
  EmrTable,
  InstructionsBox,
  FollowUpCard,
  NextStepsCard,
} from "./EmrPrimitives";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { useEMRDetailsAPI } from "@/app/hooks/api_hooks/emr/useEMRDetailsAPI";
import { useSocket } from "@/app/components/socket/SocketProvider";

/* ── Formatting helpers ─────────────────────────────── */

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  try {
    return format(new Date(dateStr), "dd MMM yyyy");
  } catch {
    return "";
  }
};

/* ── Component ──────────────────────────────────────── */
type EmrDetailsComponentProps = {
  emr_id: string;
  enableScrollSync?: boolean;
  cartId?: string | null;
  suppressScrollSyncRef?: React.MutableRefObject<boolean>;
  className?: string;
  source?: string;
};

const EmrDetailsComponent = React.forwardRef<HTMLDivElement, EmrDetailsComponentProps>(
  ({ emr_id, enableScrollSync = true, cartId, suppressScrollSyncRef }, ref) => {
    const [data, setData] = useState(null);
    const fetchEMRDetailsAPI = useEMRDetailsAPI();
    const { sendJsonMessage, readyState } = useSocket();
    const scrollRafRef = useRef<number | null>(null);

    useEffect(() => {
      fetchEMRDetailsAPI(emr_id).then(({ status, data }) => {
        if (status) {
          setData(data);
        }
      });
    }, [emr_id]);

    useEffect(() => {
      return () => {
        if (scrollRafRef.current !== null) {
          window.cancelAnimationFrame(scrollRafRef.current);
          scrollRafRef.current = null;
        }
      };
    }, []);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      if (!enableScrollSync || readyState !== WebSocket.OPEN) return;
      if (suppressScrollSyncRef?.current) return;
      const target = event.currentTarget;
      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;
        sendJsonMessage({
          event_type: "EMR_ACTION",
          payload: {
            action: "EMR_SCROLL",
            action_id: emr_id,
            cart_id: cartId ?? null,
            scrollTop: target.scrollTop,
            scrollHeight: target.scrollHeight,
            clientHeight: target.clientHeight,
          },
        });
      });
    };

    if (!data) {
      return (
        <div className="flex items-center justify-center min-h-[200px] text-muted-foreground text-lg">
          No EMR data available
        </div>
      );
    }

    const {
      customer_details,
      emr_number,
      pet_details,
      basic_checkup,
      body_condition_score,
      dental_score,
      prognosis,
      diagnosis,
      tentative_diagnosis,
      definitive_diagnosis,
      subjective_history,
      physical_examinations,
      lab_test_and_diagnostics,
      treatments,
      medicines,
      food_and_diet,
      medical_instructions,
      follow_up,
      next_steps,
    } = data;

    const hasBasicCheckup = basic_checkup && Object.values(basic_checkup).some((item: any) => hasValue(item.status));
    const hasDiagnosis = hasValue(diagnosis) || hasValue(tentative_diagnosis) || hasValue(definitive_diagnosis);
    const hasLabTests =
      hasValue(lab_test_and_diagnostics?.lab_test_added) || hasValue(lab_test_and_diagnostics?.lab_test_recommended);
    const hasTreatments = hasValue(treatments?.treatments_added);
    const hasMedicines = hasValue(medicines?.medicines_added);
    const hasFoodDiet = hasValue(food_and_diet?.food_added);

    const hasFollowUp =
      hasValue(follow_up?.follow_up_date) ||
      hasValue(follow_up?.follow_up_service) ||
      hasValue(follow_up?.follow_up_comment);

    const hasNextSteps =
      hasValue(next_steps?.suggested_services) ||
      hasValue(next_steps?.surgery_name) ||
      hasValue(next_steps?.surgery_date) ||
      hasValue(next_steps?.refer_to_reason);

    const hasScores = hasValue(body_condition_score) || hasValue(dental_score) || hasValue(prognosis);

    const getPetAge = (dob: string) => {
      const birthDate = new Date(dob);
      const today = new Date();

      let years = today.getFullYear() - birthDate.getFullYear();
      let months = today.getMonth() - birthDate.getMonth();

      // adjust if current day is before birth day
      if (today.getDate() < birthDate.getDate()) {
        months -= 1;
      }

      // adjust negative months
      if (months < 0) {
        years -= 1;
        months += 12;
      }

      return `${years} year${years !== 1 ? "s" : ""} ${months} month${months !== 1 ? "s" : ""}`;
    };

    return (
      <div
        ref={ref}
        className="w-full mx-auto max-h-[80vh] overflow-y-auto px-8 font-poppins text-foreground bg-background mt-10 mb-10"
        onScroll={handleScroll}
      >
        <div className="w-full flex flex-wrap justify-between items-center gap-3 font-roboto mb-2">
          <div className="flex flex-col gap-1 sm:gap-2">
            <img
              src="https://vetic-img.s3.amazonaws.com/emr/emrid/vetic_logo_i.png"
              alt="Billing Mirror Icon"
              className="w-[150px]  h-auto"
            />
          </div>
        </div>

        {/* ── Customer Banner ────────────────────────── */}
        <ConditionalSection show={hasValue(customer_details?.customer_name)}>
          <div className="bg-[#FFD6DB] rounded-xl px-3 py-3 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <span className="font-semibold text-[16px] sm:text-[16px] text-gray-900 break-words">
              Customer: <span className="font-normal">{customer_details?.customer_name}</span>
            </span>
          </div>
        </ConditionalSection>

        {/* ── Pet Details ────────────────────────────── */}
        <ConditionalSection show={hasValue(pet_details)}>
          <SectionTitle>Pet Details</SectionTitle>
          <InfoGrid>
            <InfoItem label="Name" value={pet_details?.pet_name} />
            <InfoItem label="Age" value={getPetAge(pet_details?.pet_age)} />
            <InfoItem label="Species" value={pet_details?.pet_specie.toUpperCase()} />
          </InfoGrid>
          <InfoGrid>
            <InfoItem
              label="Temperature"
              value={
                pet_details?.pet_temperature
                  ? `${pet_details.pet_temperature}°${pet_details.pet_temperature_unit}`
                  : undefined
              }
            />
            <InfoItem
              label="Weight"
              value={pet_details?.pet_weight_kg ? `${pet_details.pet_weight_kg} kg` : undefined}
            />
          </InfoGrid>
        </ConditionalSection>
        <ConditionalSection
          show={
            hasBasicCheckup ||
            hasScores ||
            hasDiagnosis ||
            hasValue(subjective_history) ||
            hasValue(physical_examinations) ||
            hasValue(prognosis)
          }
        >
          <SectionTitle>Basic Checkup</SectionTitle>

          {/* Preventive statuses */}
          <InfoGrid>
            <InfoItem label="Tick Prevention" value={basic_checkup?.ectoparasiticide_cover?.status} />
            <InfoItem label="Deworming" value={basic_checkup?.deworming?.status} />
            <InfoItem label="Vaccination" value={basic_checkup?.vaccination?.status} />

            <InfoItem label="Dental Score" value={hasValue(dental_score) ? `${dental_score} / 4` : undefined} />
          </InfoGrid>

          <div className="px-0 mt-4 space-y-3 text-[15px]">
            {hasValue(prognosis) && (
              <div>
                <span className="font-medium">Subjective History: </span>
                <span className="whitespace-pre-line">{subjective_history}</span>
              </div>
            )}

            {hasValue(physical_examinations) && (
              <div>
                <span className="font-medium">Symptoms & Physical Examination: </span>
                <span className="whitespace-pre-line">{physical_examinations}</span>
              </div>
            )}
          </div>
        </ConditionalSection>

        {/* ── Diagnosis ──────────────────────────────── */}
        <ConditionalSection show={hasDiagnosis}>
          <SectionTitle>Diagnosis</SectionTitle>
          {hasValue(definitive_diagnosis) && (
            <div className="mb-2 text-[15px]">
              <span className="font-medium">Definitive Diagnosis: </span>
              <span>{definitive_diagnosis.join(", ")}</span>
            </div>
          )}
          {hasValue(tentative_diagnosis) && (
            <div className="mb-2 text-[15px]">
              <span className="font-medium">Tentative Diagnosis: </span>
              <span>{tentative_diagnosis.join(", ")}</span>
            </div>
          )}
          {hasValue(diagnosis) && (
            <div className="mb-2 text-[15px]">
              <span className="font-medium">Additional Notes: </span>
              <span className="whitespace-pre-line">{diagnosis}</span>
            </div>
          )}
          {hasValue(subjective_history) && (
            <div>
              <span className="font-medium">Prognosis: </span>
              <span className="whitespace-pre-line">{prognosis}</span>
            </div>
          )}
        </ConditionalSection>

        {/* ── Lab Tests & Diagnostics ────────────────── */}
        <ConditionalSection show={hasLabTests}>
          <SectionTitle>Lab Investigations</SectionTitle>

          {hasValue(lab_test_and_diagnostics?.lab_test_added) && (
            <EmrTable
              columns={[
                { key: "name", header: "Test Name", className: "w-1/2" },
                { key: "quantity", header: "Quantity", className: "w-[16.66%]" },
                { key: "remark", header: "Instructions", className: "w-1/3" },
              ]}
              data={lab_test_and_diagnostics.lab_test_added}
            />
          )}

          {hasValue(lab_test_and_diagnostics?.lab_test_recommended) && (
            <>
              <SectionTitle>Recommmended Tests</SectionTitle>
              <EmrTable
                columns={[
                  { key: "name", header: " Name", className: "w-1/2" },
                  { key: "quantity", header: "Quantity", className: "w-[16.66%]" },
                  { key: "remark", header: "Instructions", className: "w-1/3" },
                ]}
                data={lab_test_and_diagnostics.lab_test_recommended}
              />
            </>
          )}
        </ConditionalSection>

        {/* ── Treatments ─────────────────────────────── */}
        <ConditionalSection show={hasTreatments}>
          <SectionTitle>Treatment</SectionTitle>
          <EmrTable
            columns={[
              { key: "name", header: "Name", className: "w-1/2" },
              { key: "quantity", header: "Quantity", className: "w-[16.66%]" },
              { key: "remark", header: "Remarks", className: "w-1/3" },
            ]}
            data={treatments.treatments_added}
          />
          {hasValue(treatments.treatments_instructions) && (
            <InstructionsBox>
              <span className="font-medium text-sm">Additional Treatment Instructions: </span>
              <span className="text-sm whitespace-pre-line">{treatments.treatments_instructions}</span>
            </InstructionsBox>
          )}
        </ConditionalSection>

        {/* ── Medicines ──────────────────────────────── */}
        <ConditionalSection show={hasMedicines}>
          <SectionTitle>Medication</SectionTitle>
          <EmrTable
            columns={[
              { key: "name", header: "Name", className: "w-[28%]" },
              {
                key: "dose",
                header: "Dosage",
                render: (_: any, row: any) => `${row.dose} ${row.dose_unit || ""}`,
              },
              {
                key: "frequency",
                header: "Frequency",
                render: (val: string) => `${val || ""}`,
              },
              { key: "timing", header: "Timing" },
              {
                key: "duration",
                header: "Duration",
                render: (_: any, row: any) => `${row.duration || ""} ${row.duration_unit || ""}`,
              },
              { key: "remark", header: "Remarks", render: (val) => `${val ?? ""}` },
            ]}
            data={medicines.medicines_added}
          />
          {hasValue(medicines.medicines_instructions) && (
            <InstructionsBox>
              <span className="font-medium text-sm"> Additional Medication Instructions: </span>
              <span className="text-sm whitespace-pre-line">{medicines.medicines_instructions}</span>
            </InstructionsBox>
          )}
        </ConditionalSection>

        {/* ── Medical Instructions ───────────────────── */}
        <ConditionalSection show={hasValue(medical_instructions)}>
          <SectionTitle>Medical Instructions</SectionTitle>
          <InstructionsBox>
            <p className="text-[18px]">
              <span className="font-semibold">Additional Medication Instructions:</span> {medical_instructions}
            </p>
          </InstructionsBox>
        </ConditionalSection>

        {/* ── Food & Diet ────────────────────────────── */}
        <ConditionalSection show={hasFoodDiet}>
          <SectionTitle>Food &amp; Accessories</SectionTitle>
          <EmrTable
            columns={[
              { key: "name", header: "Name", className: "w-1/2" },
              { key: "quantity", header: "Quantity", className: "w-1/4" },
              { key: "remark", header: "Remarks", className: "w-1/4" },
            ]}
            data={food_and_diet.food_added}
          />
          {hasValue(food_and_diet.food_instructions) && (
            <InstructionsBox>
              <span className="font-medium text-sm">Instructions: </span>
              <span className="text-sm whitespace-pre-line">{food_and_diet.food_instructions}</span>
            </InstructionsBox>
          )}
        </ConditionalSection>

        {/* ── Follow Up ──────────────────────────────── */}
        <ConditionalSection show={hasFollowUp}>
          <SectionTitle>Follow Up</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-2 bg-[#ECEFF3] rounded-2xl ">
            <FollowUpCard label="Service" value={follow_up?.follow_up_service} />
            <FollowUpCard label="Comment" value={follow_up?.follow_up_comment} />
          </div>
        </ConditionalSection>

        {/* ── Next Steps ─────────────────────────────── */}
        <ConditionalSection show={hasNextSteps}>
          <SectionTitle>Next Steps</SectionTitle>
          {(next_steps?.surgery_name || next_steps?.suggested_services) && (
            <NextStepsCard>
              {hasValue(next_steps?.suggested_services) && (
                <p className="text-[15px] font-medium mb-2">Surgery Suggested: {next_steps.suggested_services}</p>
              )}

              {hasValue(next_steps?.surgery_date) && (
                <p className="text-[15px]">Date: {formatDate(next_steps.surgery_date)}</p>
              )}
            </NextStepsCard>
          )}

          {/* Referral card */}
          {hasValue(next_steps?.refer_to_reason) && (
            <NextStepsCard>
              <p className="text-[15px] font-medium mb-3">Referred to Clinic:</p>

              {hasValue(next_steps?.refer_city_name) && (
                <p className="text-[15px] mb-1">City: {next_steps.refer_city_name}</p>
              )}

              {hasValue(next_steps?.refer_clinic_name) && (
                <p className="text-[15px] mb-1">Clinic: {next_steps.refer_clinic_name}</p>
              )}

              <p className="text-[15px]">Reason: {next_steps.refer_to_reason}</p>
            </NextStepsCard>
          )}
        </ConditionalSection>

        {/* ── Footer ─────────────────────────────────── */}
        <div className="mt-10 pt-4 border-t-2 border-[#E91E63] border-primary text-xs text-muted-foreground text-center">
          <p>Generated from EMR #{emr_number}</p>
        </div>
      </div>
    );
  },
);
EmrDetailsComponent.displayName = "EmrDetailsComponent";

export default EmrDetailsComponent;
